import React, { useState } from 'react';
import { ChevronLeft, AlertCircle, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ClientDetailsStep from './ClientDetailsStep';
import DriverDetailsStep from './DriverDetailsStep';
import TyreDetailsStep from './TyreDetailsStep';
import CheckList from './CheckList';
import TrolleyCheckList from './TrolleyCheckList';
import RemarksAndSignatures from './RemarksAndSignatures';
import axios from '../../../../../axiosInstance.js';
import mountainImage from '../../../../../Assets/crane/largeRecoveryVan.avif';
import TyreImage from './TyreImage.jsx';

const GeneralInspectionPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState({
    // Client Details
    clientDetails: {
      customerPhone: '',
      customerName: '',
      address: '',
      city: '',
      state: '',
      siteManager: ''
    },

    // Driver Details
    driverDetails: {
      inspectionDate: '',
      driverName: '',
      mobileNo: '',
      vehicleNo: '',
      model: '',
      vehicleType: '',
      clientManager: ''
    },

    // ULIP vehicle data (auto-populated from API)
    vehicleUlipData: null,

    // Tyre Details (fixed 6 tractor tyres; trailer tyres added dynamically)
    tyres: {
      FL: {},
      FR: {},
      DLO: {},
      DLI: {},
      DRI: {},
      DRO: {},
    },
    additionalNotes: '',

    // Checklist
    checklist: [],

    // Trolley Checklist
    trolleyChecklist: [],
    trolleyImages: {},
    trolleyImagePreviews: {},

    // Remarks & Signatures
    remarksAndSignatures: {
      driverProblems: '',
      supervisorRemarksNote: '',
      driverSignature: '',
      managerSignature: '',
      supervisorSignature: '',
      driverSignatureImg: '',
      managerSignatureImg: '',
      supervisorSignatureImg: ''
    }
  });

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');
    console.log('Form data to submit:', formData);

    try {
      const vendorCode = localStorage.getItem('userId');
      
      const payload = {
        vendorCode,
        clientDetails: {
          customerPhone: formData.clientDetails.customerPhone,
          customerName: formData.clientDetails.customerName,
          address: formData.clientDetails.address,
          city: formData.clientDetails.city,
          state: formData.clientDetails.state,
          siteManager: formData.clientDetails.siteManager
        },
        driverDetails: {
          inspectionDate: formData.driverDetails.inspectionDate,
          driverName: formData.driverDetails.driverName,
          mobileNo: formData.driverDetails.mobileNo,
          vehicleNo: formData.driverDetails.vehicleNo,
          vehicleModel: formData.driverDetails.model,
          vehicleType: formData.driverDetails.vehicleType,
          clientManager: formData.driverDetails.clientManager
        },
        tyres: formData.tyres,
        additionalNotes: formData.additionalNotes,
        checklist: formData.checklist,
        trolleyChecklist: formData.trolleyChecklist,
        remarksAndSignatures: {
          driverProblems: formData.remarksAndSignatures.driverProblems,
          supervisorRemarksNote: formData.remarksAndSignatures.supervisorRemarksNote,
          driverSignature: formData.remarksAndSignatures.driverSignature,
          managerSignature: formData.remarksAndSignatures.managerSignature,
          supervisorSignature: formData.remarksAndSignatures.supervisorSignature,
          driverSignatureImg: formData.remarksAndSignatures.driverSignatureImg || '',
          managerSignatureImg: formData.remarksAndSignatures.managerSignatureImg || '',
          supervisorSignatureImg: formData.remarksAndSignatures.supervisorSignatureImg || ''
        }
      };

      console.log('Submitting payload:', payload);

      // Call your API endpoint to save the inspection data
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/submit`,
        payload
      );

      if (response.data.status) {
        console.log('Inspection submitted successfully:', response.data);
        setSubmitMessage('General inspection submitted successfully!');
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        setSubmitError(response.data.message || 'Failed to submit inspection');
      }
    } catch (error) {
      console.error('Error submitting inspection:', error);
      setSubmitError(
        error.response?.data?.message || 
        'An error occurred while submitting the inspection. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ClientDetailsStep
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <DriverDetailsStep
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <CheckList
            onPrevious={handlePreviousStep}
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <TrolleyCheckList
            onPrevious={handlePreviousStep}
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 5:
        return (
          <TyreImage
            onPrevious={handlePreviousStep}
            formData={formData}
            setFormData={setFormData}
            onNext={handleNextStep}
          />
        );
      case 6:
        return (
          <RemarksAndSignatures
            onPrevious={handlePreviousStep}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // Show success message
  if (submitMessage) {
    return (
      <div className="font-satoshi w-full max-w-md mx-auto px-4 pb-4 pt-[6rem] bg-white min-h-screen text-gray-800 flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Success!</h3>
          <p className="text-gray-600">{submitMessage}</p>
        </div>
      </div>
    );
  }

  // Show error message
  if (submitError) {
    return (
      <div className="font-satoshi w-full max-w-md mx-auto px-4 pb-4 pt-[6rem] bg-white min-h-screen text-gray-800 flex flex-col items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{submitError}</p>
          <button
            onClick={() => {
              setSubmitError('');
              setCurrentStep(5);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-satoshi bg-white min-h-screen">
      {/* Desktop Layout */}
      <div className="hidden md:block h-screen bg-gradient-to-br from-blue-500 via-blue-400 to-cyan-300 relative overflow-hidden">
        {/* Background Image */}
        <img
          src={mountainImage}
          alt="General Inspection"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 to-transparent"></div>
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 z-30 bg-white/90 backdrop-blur-sm hover:bg-white p-3 rounded-full transition-all shadow-lg"
        >
          <ChevronLeft size={24} className="text-gray-700" />
        </button>

        {/* Form Container - Overlapping design */}
        <div className="absolute right-0 top-0 h-full w-full max-w-2xl">
          <div className="h-full flex items-center justify-end pr-8">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto overflow-x-hidden border border-white/20">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full flex items-center justify-center">
                    <ClipboardCheck size={22} className="text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">VEHICLE INSPECTION</h2>
                </div>
                <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"></div>
              </div>
              {renderStep()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden w-full max-w-md mx-auto px-4 pb-4 pt-[3rem] bg-white min-h-screen overflow-x-hidden">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="hover:bg-gray-100 p-2 rounded-full transition"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full flex items-center justify-center">
            <ClipboardCheck size={16} className="text-white" />
          </div>
          <h1 className="text-xl font-bold">Vehicle Inspection</h1>
        </div>
        {renderStep()}
      </div>
    </div>
  );
};

export default GeneralInspectionPage;
